<template>
    <v-container>
        <div id="vis-container">
            
          <svg class="svg-frame2" width="100%" id="timeline-container"></svg>
            <div class="tooltip" id="timeline-tooltip" style="pointer-events: none;"></div>
            <div style="overflow:scroll; direction: rtl; "><svg class="svg-frame"></svg></div>
        
        </div>
    </v-container>
</template>


<script>

import * as d3Base from 'd3';
import * as d3Collection from 'd3-collection';
import _ from 'lodash';
import * as d3Dag from 'd3-dag';

export default {
    data() {
        return {
            env: null,
            colors: ["#7fc97f", "#beaed4", "#fdc086", "#ffff99", "#386cb0", "#f0027f", "#bf5b17", "#666666"],
            svg: null,
            tooltip: null,
            x: null,
            y: null,
            xAxisTop: null,
            xAxisBottom: null,
            grid: null,
            margin: null,
            timelineHeight: null,
            timelineWidth: null,
            d3: null,
            sortedData: null,
            recordContainer: null
        }
    },
    async mounted() {
        const d3 = Object.assign({}, d3Base, d3Dag);
        this.d3 = d3;

        var arrayData = await this.retrieveArrayData(d3);

        const data = arrayData;

        const dag = d3.dagStratify()(data);

        

//       .decross( d3.decrossTwoLayer().order(d3.twolayerOpt().large("large")))
        const nodeRadius = 15;
        const layout = d3
            .sugiyama()
            .layering(d3.layeringSimplex())
            .decross( d3.decrossTwoLayer().order(d3.twolayerGreedy().base(d3.twolayerAgg())))
            //.decross(d3.decrossTwoLayer().order(d3.twolayerOpt().large("medium")))
            .coord(d3.coordSimplex())
            .nodeSize((node) => [(node ? 3.6 : 0.25) * nodeRadius, 3 * nodeRadius]);

        const { width, height } = layout(dag);

        const svgSelection = d3.select("svg.svg-frame");
        svgSelection.attr("height", height);
        svgSelection.attr("width", width);

        const defs = svgSelection.append("defs");
        const steps = dag.size();
        const interp = d3.interpolateRainbow;
        const colorMap = new Map();

        for (const [i, node] of dag.descendants().entries()) {
            colorMap.set(node.data.id, interp(i / steps));
        }

        // How to draw edges
        const line = d3
            .line()
            .curve(d3.curveMonotoneY)
            .x((d) => d.x)
            .y((d) => d.y);

        // Plot edges
        svgSelection.append("g")
            .selectAll("path")
            .data(dag.links())
            .enter()
            .append("path")
            .attr("d", ({ points }) => line(points))
            .attr("fill", "none")
            .attr("stroke-width", 3)
            .attr("stroke", ({ source, target }) => 
            {
                // encodeURIComponents for spaces, hope id doesn't have a `--` in it
                const gradId = encodeURIComponent(`${source.data.id}--${target.data.id}`);

                const grad = defs.append("linearGradient")
                    .attr("id", gradId)
                    .attr("gradientUnits", "userSpaceOnUse")
                    .attr("x1", source.x)
                    .attr("x2", target.x)
                    .attr("y1", source.y)
                    .attr("y2", target.y);

                grad.append("stop")
                    .attr("offset", "0%")
                    .attr("stop-color", colorMap.get(source.data.id));

                grad.append("stop")
                    .attr("offset", "100%")
                    .attr("stop-color", colorMap.get(target.data.id));

                return `url(#${gradId})`;
            });

        // Select nodes
        const nodes = svgSelection.append("g")
            .selectAll("g")
            .data(dag.descendants())
            .enter()
            .append("g")
            .attr("transform", ({ x, y }) => `translate(${x}, ${y})`);

        // Plot node circles
        nodes.append("circle")
            .attr("r", nodeRadius)
            .attr("fill", (n) => colorMap.get(n.data.id));

        const arrowSize = (nodeRadius * nodeRadius) / 5.0;
        const arrowLen = Math.sqrt((4 * arrowSize) / Math.sqrt(3));
        const arrow = d3.symbol().type(d3.symbolTriangle).size(arrowSize);

        svgSelection.append("g")
            .selectAll("path")
            .data(dag.links())
            .enter()
            .append("path")
            .attr("d", arrow)
            .attr("transform", ({ source, target, points }) => {
                const [end, start] = points.slice().reverse();
                // This sets the arrows the node radius (20) + a little bit (3) away from the node center, on the last line segment of the edge. This means that edges that only span ine level will work perfectly, but if the edge bends, this will be a little off.
                const dx = start.x - end.x;
                const dy = start.y - end.y;
                const scale = (nodeRadius * 1.15) / Math.sqrt(dx * dx + dy * dy);
                // This is the angle of the last line segment
                const angle = (Math.atan2(-dy, -dx) * 180) / Math.PI + 90;

                return `translate(${end.x + dx * scale}, ${end.y + dy * scale}) rotate(${angle})`;
            })
            .attr("fill", ({ target }) => colorMap[target.data.id])
            .attr("stroke", "white")
            .attr("stroke-width", 1.5)
            .attr("stroke-dasharray", `${arrowLen},${arrowLen}`);

        // Add text to nodes
        nodes.append("text")
            .text((d) => d.data.id)
            .attr("font-weight", "bold")
            .attr("font-family", "sans-serif")
            .attr("text-anchor", "middle")
            .attr("alignment-baseline", "middle")
            .attr("fill", "white");

        const nodeCapture = dag.descendants();
        const linkCapture = dag.links();

        const nodeGroups = _.chain(nodeCapture).groupBy(data => data.x).map((value, key) => ({ x: key, items: value.map(item => item.data.id) })).value();
        const dataWithX = data.map(data => {
            var match = nodeGroups.find(node => node.items.some(c => c === data.id)).x;
            data.line = match;
            return data;
        });

        var sortedData = dataWithX.slice().sort((a, b) => d3.ascending(a.line, b.line));

        sortedData.forEach(
            (d) => (d.color = '#c10000')
        );
        
        this.sortedData = sortedData;

        this.timelineWidth = document.getElementById('timeline-container').clientWidth;
        this.timelineHeight = 30 * sortedData.length;
        this.margin = ({left: 0.01 * this.timelineWidth, right: 0.01 * width, top: 50, bottom: 0.01 * this.timelineHeight});

        const y = d3
            .scaleBand()
            .domain(d3.range(arrayData.length))
            .range([this.timelineHeight - this.margin.bottom, this.margin.top])
            .padding(0.2);

        //create svg
        const svg = d3Base.select("svg.svg-frame2")
         //  .attr('height', this.timelineHeight)
        .attr('height', window.innerHeight - 150)
         .attr("viewBox", [0, 0,this.timelineWidth, this.timelineHeight])
         .attr('preserveAspectRatio', "xMidYMin slice ")
            .style("font", "0.85em sans-serif")
            .style("background", "white");
        this.svg = svg;

        //div.setAttribute('style','height: 700px;overflow:scroll');

      

        //create the grid to hold the timeline
        this.grid = this.svg
            .append("g")
            .attr("cursor", "grab")
            .attr("transform", `translate (${this.margin.left} ${this.margin.top - 5})`);

        //set up tooltips
        var tooltip = d3.select(document.getElementById("timeline-tooltip"));
        this.tooltip = tooltip;

        tooltip.style("position", "absolute")
            .style("top", 0)
            .style("font", "0.925em sans-serif")
            .style("visibility", "hidden")
            .style("background", "white")
            .style("border-radius", "5px")
            .style("padding", "10px")
            .style("box-shadow", "0 0 10px gray")
            .style("max-width", this.timelineWidth * 0.4 + "px")

var outerGroup = this.svg.append("g");
this.recordContainer = outerGroup;

        //create groups based on the data, can hold multiple rects on one y point
        var groups = outerGroup.selectAll("g").data(sortedData).enter();

        var incidents = groups
            .append("g")
            .attr("transform", (d, i) => `translate (${this.margin.left} ${y(i)})`) // move bars to their y-coord
            .attr("yC",(d, i) => `${y(i)}`)
            .attr("line", function (d) { return d.line; })
            .attr("link", function (d) { return d.parentIds.join(','); })
            .attr("id", function (d) { return d.id })
            .attr("class", "incs rectangle");

        //change sorting order of bars, based on sortBy
        d3.selectAll(".incs")
            .data(data, (d) => d.id)
            .transition()
            .attr("transform", (d, i) => `translate(${this.margin.left} ${y(i)})`);

        //change color of bars, based on colorBy
        d3.selectAll(".incs > rect")
            .transition()
            .attr("fill", (d) => d.color);

        var linkArray = [];

        incidents.each(function (d) {
            var incident = d3.select(this).style("cursor", "pointer");

            // create bars inside group
            incident
                .append("rect")
                .attr("height", y.bandwidth())
                .attr("fill", d.color)
                .style("opacity", 0.6);

            // create incident text
            incident
                .append("text")
                .text(d.Name)
                .attr("y", "12px")
                .style("text-anchor", "end")
                .style("dominant-baseline", "middle");

            d.parentIds.forEach(function (linkId) {
                linkArray.push("p-" + d.id + "-"+ linkId);
            });
        });

          //make the top axis
        this.xAxisTop = this.svg
            .append("g")
            .attr("transform", `translate (${this.margin.left} ${this.margin.top - 5})`);

        this.xAxisTop.append("rect")
            .attr("height", "50")
            .attr("width", "100%")
            .attr("fill", "#eaeaea")
            .attr("transform", "translate(-18,-50)");

        //make the top axis
        this.xAxisBottom = this.svg
            .append("g")
            .attr("transform", `translate (${this.margin.left} ${window.innerHeight - 190})`);

        this.xAxisBottom.append("rect")
            .attr("height", "50")
            .attr("width", "100%")
            .attr("fill", "#eaeaea")
              .attr("transform", "translate(-18,0)");

const self = this
                            
    // === INTERACTIVITY ===
    const mouseMove = function (event) {
      var [x, y] = d3.pointer(event);

      //show the tooltip on the left if it will fit
      if (x > self.timelineWidth * 0.5) {
        self.tooltip.style("left", "auto").style("right", self.timelineWidth - x + 10 + "px");
      } 
      //otherwise on the right
      else {
        self.tooltip.style("right", "auto").style("left", x + 10 + "px");
      }

      //show tooltip above if possible
      if (y > self.timelineHeight * 0.5) {
        self.tooltip.style("top", "auto").style("bottom", self.timelineHeight  - y + 10 + "px");
      } 
      //otherwise below
      else {
        self.tooltip.style("bottom", "auto").style("top", y + 10 + "px");
      }
    };

     svg.on("mousemove", mouseMove);

        // --- link INTERACTIONS ---
        const linkMouseOut = function (_, d) {
            self.tooltip.style("visibility", "hidden");
        };

        const linkMouseOver = function (_, d) {
            var pathId = _.toElement.getAttribute('pathId');
            self.tooltip.style("visibility", "visible").html(self.getLinkTooltip(pathId)); // small tooltip
        };

        linkArray.forEach(function (link) {
            outerGroup.append("path")
                .attr("class", "path")
                .attr("pathId", link)
                .attr("d", "")
                .attr("marker-end", "")
                .attr("stroke", "")
                .attr("fill", "")
                .on("mouseout", linkMouseOut).on("mouseover", linkMouseOver);
        });

        
                // --- INCIDENT INTERACTIONS ---
                const incMouseOut = function (_, d) {
                self.tooltip.style("visibility", "hidden");
                d3.select(this.parentNode)
                    .select("rect")
                    .style("fill", function(d){return d3.rgb(d.color)});
                };



                const incMouseOver = function (_, d) {
                self.tooltip.style("visibility", "visible").html(self.getIncidentTooltip(d)); 
                d3.select(this.parentNode)
                    .select("rect")
                    .style("fill", function(d){return d3.rgb(d.color).darker(1);});
                };

            const incs = incidents.selectAll(".incs text, .incs rect");

//set up events
    incs.on("mouseleave", incMouseOut).on("mouseenter", incMouseOver);

        this.x = d3
            .scaleUtc()
            .domain([
                d3.min(sortedData.map((d) => d["StartDate"])),
                d3.max(sortedData.map((d) => d["EndDate"]))
            ])
            .range([this.margin.left, this.timelineWidth - this.margin.right])
            .nice();


        const zoom = d3
            .zoom()
            .scaleExtent([0.75, 112]) // set limits of unzoom and zoom
            .extent([
                [this.margin.left, 0],
                [this.timelineWidth - this.margin.right, this.timelineHeight]
            ])
            .on("zoom", this.update);

        svg.call(zoom).transition().duration(1000).call(zoom.scaleTo, 1);
        svg.call(zoom).transition().duration(1000).call(zoom.scaleTo, 1);

        //change sorting order of bars, based on sortBy
            d3.selectAll(".incs")
            .data(sortedData, (d) => d.id)
            .transition()
            .attr("transform", (d, i) => `translate(${this.margin.left} ${y(i)})`);
        },
    methods: {
        async retrieveArrayData(d3){
       
            const data = ( await d3.csv(
                "https://raw.githubusercontent.com/astridf/Assorted/main/c-data.csv",
                d3.autoType
                )
            );

            let parseTime = d3.timeParse("%d/%m/%Y")
            
            data.forEach((d) => {
            d["rank"] = d["Rank"];
                if (d["Earlier committees"] != null){
                    d["parentIds"] = d["Earlier committees"].split(",");
                }
                else {
                    d["parentIds"] = [];
                }
                
                d["StartDate"] = parseTime(d["Date established"]);
                if (d["Date ceased"] != null){
                    d["EndDate"] = parseTime(d["Date ceased"]);
                }
                else {
                    var today = new Date();
                    var dd = String(today.getDate()).padStart(2, '0');
                    var mm = String(today.getMonth() + 1).padStart(2, '0'); //January is 0!
                    var yyyy = today.getFullYear();
                    
                    today = dd + '/' + mm + '/' + yyyy;
                    
                    d["EndDate"] = parseTime(today);
                }
                
            });
            return data;
        },
      
        update(event) {
            var newX = event.transform.rescaleX(this.x);
  
            this.recordContainer.attr("transform", "translate(0," + event.transform.y + ")");
  
            // update axes
            this.xAxisTop.call(this.d3.axisTop(newX).tickSizeOuter(0));
            this.xAxisBottom.call(this.d3.axisBottom(newX).tickSizeOuter(0));

            // update gridlines
            this.grid.call(
                this.d3.axisTop(newX)
                .tickFormat("")
                .tickSize(-(this.timelineHeight - this.margin.top - this.margin.bottom + 10))
                .tickSizeOuter(0)
            );
            this.grid.selectAll("line").style("stroke", "gray").style("stroke-width", 0.1);

            // update data positions

            this.svg
                .selectAll(".incs")
                .attr("xC", (d) => newX(d["StartDate"]))
                .attr("widthC", (d) => {
                var days = newX(d["EndDate"]) - newX(d["StartDate"]) - 10;
                return days > 5
                    ? days
                    : 5;
                });
        
            this.svg
                .selectAll(".incs rect")
                .attr("x", (d) => newX(d["StartDate"]))
                .attr("width", (d) => {
                var days = newX(d["EndDate"]) - newX(d["StartDate"]) - 10;
                return days > 5
                    ? days
                    : 5;
                });

            this.svg.selectAll(".incs text").attr("x", (d) => newX(d["StartDate"]) - 10);
            this.drawLinks();
       
      
    },

    formatDate (d)  {
        const [begin, end] = [this.convert(d["StartDate"]), this.convert(d["EndDate"])];

        return begin === end ? begin : `${begin} – ${end}`;
    },
    convert (date) {
        return date.toLocaleDateString("US", {
            year: "numeric",
            month: "short",
            day: "numeric"
        });
    },
    getIncidentTooltip(d)  {
        const info = {
            "Committee" : d.id + ' - ' + d.Name,
            "Dates": this.formatDate(d),
            Location: d.location
        };

        const arr = Object.entries(info).map(([key, value]) =>
            value ? `<b>${key}:</b> ${value}` : null
        );

        return arr.filter(Boolean).join("<br/>");
    },
    getLinkTooltip (d) {
        var linkFrom = d.replace('p-','').split('-')[1];
        var linkTo = d.replace('p-','').split('-')[0];
        var linkFromData = this.sortedData.find(d => d.id == linkFrom);
        var linkToData = this.sortedData.find(d => d.id == linkTo);
        
        const info = {
            "From" : linkFromData.Name + " (" + linkFromData.id + " - " + this.formatDate(linkFromData) + ")",
            "----To" : linkToData.Name + " (" + linkToData.id + " - " + this.formatDate(linkToData) + ")",
        };

        const arr = Object.entries(info).map(([key, value]) =>
            value ? `<b>${key}:</b> ${value}` : null
        );

        return arr.filter(Boolean).join("<br/>");
    },
        resolveOverlaps(d3, env) {
            for (var t = 0; t < env.structData.length; t++) {
                //env.structData[t].line = null  
                env.structData[t].sub = null  

            }

            env.flatData = []

            env.structData.sort(function (a, b) {
                return d3.ascending(a.start, b.start);
            });

            var keys = env.structData.map(function (d) { return d[env.category]; })
                .reduce(function (p, v) { return p.indexOf(v) == -1 ? p.concat(v) : p; }, [])
                .filter(function (d) { return (typeof d !== "undefined") ? d !== null : false });

            keys.forEach(function (key) {
                var items = env.structData.filter(function (d) { return d[env.category] === key; });
                var i, line, count = [], lines = [];

                var top = d3.max(env.structData, function (d) { return d.line }) || 0;        
              
                items.forEach(function (item) {                  
                    for (i = 0, line = 0; i < top; i++ , line++) {  
                   
                        if (item.finish <= lines[i]) { break; }
                    }
               
                    //item.line = item.line;           
                    item.sub = item[env.category] + '+&+' + item.line
                    lines[line] = item.start;
                    count.push(item.line);
                })
          
                env.flatData.push({ category: items[0][env.category], count: count.length })
            });
           
            env.flatData.sort(function (a, b) {
                return d3.ascending(a.category, b.category);
            });
        },
        drawLinks() {      

            var d3 = this.d3;
           this.d3.selectAll(".incs").each(function(d,i) {
            var thisSelect = d3.select(this);
                
                if (thisSelect.attr("link") !== ''){

                
                    var linkFrom = d3.select(this);
                    var linkNum = d3.select(this).attr("link");
                    var linkId = d3.select(this).attr("id");
                    var splitLink = linkNum.split(',');

                    splitLink.forEach(function (linkNumInner) {
                    var linkTo = d3.select('.rectangle[id="' + linkNumInner + '"]');

                    
                    if (linkTo != null && linkTo._groups != null && linkTo._groups[0][0] !== null){
                        var path = d3.select('.path[pathId="p-' + linkId + '-' + linkNumInner + '"]');
            
                        const linkSource = {
                        x: parseFloat(linkFrom.attr('xC')) + 20,
                        y: parseFloat(linkFrom.attr('yC')) + 10
                        };

                        const linkTarget = {
                        x: parseFloat(parseFloat(linkTo.attr('xC')) + parseFloat(linkTo.attr('widthC'))) + 12,
                        y: parseFloat(linkTo.attr('yC')) + 10
                        };

                        const link = d3
                        .linkHorizontal()
                        .x(d => d.x)
                        .y(d => d.y)({
                        source: linkSource,
                        target: linkTarget
                        });

                        
                        
                    path
                        .attr('d', link)
                        .attr('marker-end', 'url(#arrow)')
                        .attr('stroke', '#c10000')
                        .attr('stroke-width', '4')
                        .attr('opacity', '0.3')
                        .attr('fill', 'none');

                    }
                    });
                }
                });
        },
    }
}
</script>